/// <summary>
/// Author: -
/// </summary>
import axios from "axios";
import { ApiKey } from "./Constant.js";
import * as AppActions from "../redux/AppAction";
import * as AuthActions from "../redux/AuthAction";
import store from "../ApplicationStore";
import i18n from "i18next";
import { logApiCallToLocalStorage } from "./ClientLogger.js"; // your localStorage logging function
import { setPlayerCounterServerUtc } from "./PlayerCounterUtil.js";

const _HTTP_CODE_UNAUTHORIZED = 401;
const _HTTP_CODE_FORBIDDEN = 403;
const _HTTP_CODE_TOO_MANY_REQUEST = 429;

function trimApiMessage(s) {
  if (typeof s !== "string") return "";
  const t = s.trim();
  return t || "";
}

function textFromErrorValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return trimApiMessage(value);
  if (typeof value !== "object") return "";
  if (trimApiMessage(value.message)) return trimApiMessage(value.message);
  if (trimApiMessage(value.detail)) return trimApiMessage(value.detail);
  if (trimApiMessage(value.title)) return trimApiMessage(value.title);
  try {
    return trimApiMessage(JSON.stringify(value));
  } catch {
    return "";
  }
}

function looksLikeTranslationKey(s) {
  if (typeof s !== "string") return false;
  const n = s.replace(/\.$/, "").trim();
  return /^[A-Z][A-Z0-9_]*$/.test(n);
}

/**
 * One place to turn mixed API / axios error shapes into a string message + whether showResponseMessage should i18n.t it.
 * HTTP fallback only for status >= 400 so 200 + success:false does not become "HTTP 200".
 */
export function normalizeApiFailurePayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { message: "", translateMessage: true };
  }

  const apiMessage = payload[ApiKey._API_MESSAGE_KEY];
  let out = "";

  if (typeof apiMessage === "string") {
    out = trimApiMessage(apiMessage);
  } else if (apiMessage != null && typeof apiMessage === "object") {
    out = textFromErrorValue(apiMessage);
  }

  if (!out) {
    const dataField = payload[ApiKey._API_DATA_KEY];
    if (dataField !== undefined) {
      out = textFromErrorValue(dataField);
    }
  }
  if (!out && payload.data !== undefined) {
    out = textFromErrorValue(payload.data);
  }

  const st = Number(payload.status);
  if (!out && Number.isFinite(st) && st >= 400) {
    out = `Request failed (HTTP ${st}).`;
  }

  // Still nothing from server — show a generic prompt (do not append when out is non-empty).
  if (!out) {
    out = "PLEASE_TRY_AGAIN";
  }

  const translateMessage = looksLikeTranslationKey(out);
  return { message: out, translateMessage };
}

function finalizeApiFailureEnvelope(payload) {
  const merged = { success: false, ...(payload && typeof payload === "object" ? payload : {}) };
  if (merged.success !== false) {
    return payload;
  }
  const norm = normalizeApiFailurePayload(merged);
  return {
    ...merged,
    message: norm.message,
    translateMessage: norm.translateMessage,
  };
}

/// <summary>
/// Author: -
/// </summary>
async function interceptRequest(requestConfig) {
  //store.dispatch(AppActions.setBusy());
  return requestConfig;
}

/// <summary>
/// Author: -
/// </summary>
function interceptResponse(response) {
  const serverDate = response?.headers?.date;
  const serverUtcTimestampMs = serverDate ? Date.parse(serverDate) : NaN;

  if (Number.isFinite(serverUtcTimestampMs)) {
    setPlayerCounterServerUtc(serverUtcTimestampMs);
  }

  store.dispatch(
    AppActions.setPerformApiCall(
      response.data[ApiKey._API_SUCCESS_KEY],
      response.config.url
    )
  );

  //store.dispatch(AppActions.setIdle());
  const data = response.data;
  if (data && data[ApiKey._API_SUCCESS_KEY] === false) {
    return finalizeApiFailureEnvelope({
      ...data,
      status: response.status,
    });
  }
  return data;
}

/// <summary>
/// Author: -
/// </summary>
function interceptError(error) {
  var returnData = {};

  store.dispatch(AppActions.setIdle());
  if (error && error.response) {
    // CHRIS TODO 20251211: Temporarily disable API Call logging first, need to plan how to save these data
    // // Log failed API call
    // logApiCallToLocalStorage({
    //   url: error.config?.url,
    //   method: error.config?.method,
    //   status: error.response?.status,
    //   requestBody: error.config?.data,
    //   responseBody: JSON.stringify(error.response?.data).slice(0, 500),
    //   success: false
    // });

    if (error.response.status == _HTTP_CODE_UNAUTHORIZED) {
      store.dispatch(AuthActions.performLogout());
      store.dispatch(
        AppActions.showResponseMessage(false, i18n.t("SESSION_EXPIRED"))
      );
      returnData = {
        success: false,
        message: i18n.t("SESSION_EXPIRED"),
      };
    } else if (error.response.status == _HTTP_CODE_FORBIDDEN) {
      store.dispatch(AuthActions.performLogout());
      store.dispatch(
        AppActions.showResponseMessage(false, i18n.t("PLEASE_TRY_AGAIN_LATER"))
      );
      returnData = {
        success: false,
        message: i18n.t("PLEASE_TRY_AGAIN_LATER"),
      };
    }else if (error.response.status == _HTTP_CODE_TOO_MANY_REQUEST) {
      // store.dispatch(AuthActions.performLogout());
      store.dispatch(
        AppActions.showResponseMessage(false, i18n.t("TOO_MANY_REQUESTS"))
      );
      returnData = {
        success: false,
        message: i18n.t("TOO_MANY_REQUESTS"),
      };
    }else{
      const d = error.response?.data;
      const messageStr =
        typeof d === "string"
          ? d
          : d != null && typeof d === "object" && typeof d.message === "string"
            ? d.message
            : "";
      returnData = {
        success: false,
        status: error.response.status,
        message: messageStr,
        data: d != null && typeof d === "object" ? d : undefined,
      };
    }

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify(error.response));
    }
  } else if (error.config) {
    returnData = {
      success: false,
      status: 0,
      message: error.code === "ECONNABORTED" ? "PLEASE_TRY_AGAIN" : (error.message || "PLEASE_TRY_AGAIN"),
    };
  }

  // if (!store.getState().authState.heartBeat) {
  //   store.dispatch(AuthActions.setToggleLoginModal(true));
  // }
  return finalizeApiFailureEnvelope(returnData);
}

//const baseURL = "https://staging.riocity9.com";
const baseURL = "";

const ApiEngine = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "X-Requested-With": ApiKey._API_XML_HTTP_REQUEST_KEY,
  },
});

ApiEngine.interceptors.request.use(
  async (config) => {
    if (!navigator.onLine) {
      store.dispatch(AppActions.showResponseMessage(false, i18n.t("NO_INTERNET")));
      return Promise.reject(
        finalizeApiFailureEnvelope({
          success: false,
          message: "NO_INTERNET",
          status: 0,
        })
      );
    }
    return interceptRequest(config);
  },
  (error) => Promise.reject(error)
);

ApiEngine.interceptors.response.use(
  (response) => interceptResponse(response),
  (error) => interceptError(error)
);

export default ApiEngine;
export { baseURL };
